import get from 'lodash/get';
import {useState} from 'react';
import {useGetCPTCodesQuery} from '../../redux/services/codeApi';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';

import CustomNumberField from '../common/CustomNumberField';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';

// these codes only accept a units value of 1
const singleUnitCodes = [
  '97161',
  '97162',
  '97163',
  '97164',
];

const excludeCodes = [
  '97003',
  '97004',
];

const SingleCodeEditor = props => {
  const {sessionCode: c} = props;
  const childProps = {
    options: props.options,
    multi: false,
    disabled: props.disabled || false,
  };
  // eslint-disable-next-line no-unused-vars
  const formattedOptions = (props?.options || [])
    .map(opt => ({...opt, label: (opt?.label || '').replace(/physical therapy/i, 'PT')}))
    .filter(opt => !excludeCodes.includes(opt.value));
  return (
    <Grid key={c.code} container spacing={1} alignItems={'center'}>
      <Grid item xs={12} md={6}>
        <Box style={{width: '100%'}}>
          <CustomSelect
            {...childProps}
            options={formattedOptions}
            label='Code'
            disableClearable
            fullWidth={true}
            disabled={childProps.disabled}
            // sx={{minWidth: 130, width: '100%'}}
            size='small'
            getOptionDisabled={opt => (props.sessionCodes || [])
              .map(_c => _c.code)
              .includes(opt.value)
            }
            defaultValue={c?.code}
            onChange={e => {
              // prevent text change events from getting emitted since they cause the
              // sessionNote form to update the draft every time and lose focus
              if (e?.nativeEvent?.inputType === 'insertText') return;
              props.onChange({...c, code: e.target.value});
            }}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={3}>
        <CustomTextField
          fullWidth={true}
          label='Modifier'
          size='small'
          disabled={childProps.disabled}
          defaultValue={get(c, 'modifier') || null}
          onChange={e => props.onChange({...c, modifier: e.target.value})}
          sx={{input: {padding: '7px 14px !important'}}}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <CustomNumberField
          fullWidth={true}
          label='Units'
          defaultValue={get(c, 'units') || '1'}
          size='small'
          min={1}
          max={9}
          step={1}
          maxLength={1}
          disabled={childProps.disabled || singleUnitCodes.includes(c.code)}
          onChange={e => props.onChange({...c, units: e.target.value})}
        />
      </Grid>
      <Grid item xs={12} md={1}>
        <IconButton
          color='error'
          disabled={childProps.disabled}
          onClick={() => props.onDelete(c)}
          style={{marginTop: '1.2em'}}
        >
          <DeleteIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

const handleAdd = ({sessionCodes, setSessionCodes}) => () => {
  setSessionCodes([...sessionCodes, {code: null, modifier: null, units: 1}]);
};

const handleChange = ({onChange, sessionCodes, setSessionCodes}) => i => val => {
  let newCodes = [...sessionCodes];
  newCodes[i] = val;
  if (singleUnitCodes.includes(val.code)) val.units = 1;
  setSessionCodes(newCodes);
  onChange({target: {value: newCodes}});
};

const handleDelete = ({sessionCodes, setSessionCodes, onChange}) => code => {
  const newCodes = sessionCodes.filter(s => s.code !== code.code);
  setSessionCodes(newCodes);
  onChange({target: {value: newCodes}});
};

const CPTSelect = props => {
  const [sessionCodes, setSessionCodes] = useState(props.defaultValue);
  const cptRes = useGetCPTCodesQuery();
  const procedureCodes = get(cptRes, 'data.data');
  if (cptRes.isFetching || cptRes.isLoading) return <></>;
  const sessionCodesWithPlaceholder = (
    !sessionCodes || sessionCodes.length === 0
    ? [{code: null, modifier: null, units: 1}]
    : sessionCodes
  );
  return (
    <Stack>
      {(sessionCodesWithPlaceholder || []).map((c, i) =>
        <>
          {i > 0 ? <Divider style={{marginTop: '1rem'}}/> : null}
          <SingleCodeEditor
            key={c.code}
            index={i}
            sessionCode={c}
            sessionCodes={sessionCodes}
            disabled={props.disabled}
            options={(procedureCodes || [])
              // ignore codes already selected
              .filter(cpt =>
                sessionCodes && sessionCodes.length
                ? !sessionCodes.map(_c => _c.code).includes(cpt.code) || cpt.code === c.code
                : true
              )
              .map(cpt => ({
                value: cpt.code,
                label: `${cpt.code} - ${cpt.description}`,
              }))
            }
            onDelete={handleDelete({
              sessionCodes,
              setSessionCodes,
              onChange: props.onChange
            })}
            onChange={handleChange({
              onChange: props.onChange,
              sessionCodes,
              setSessionCodes,
            })(i)}
          />
        </>
      )}
      <Box paddingTop={1.5}>
        <Button
          variant='primaryOutline'
          disabled={props.disabled}
          onClick={handleAdd({sessionCodes, setSessionCodes})}
          startIcon={<AddIcon/>}
        >          
          Add More
        </Button>
      </Box>
    </Stack>
  );
};

export default CPTSelect;
